import {Injectable} from '@angular/core';
import {Observable, of, throwError} from 'rxjs';
import {UploadLinkModel} from '../models/upload-link.model';
import {API} from '../../shared/constants/api.constants';
import {ErrorHandlingHttpClient} from '../../shared/services/error-handling-http-client.service';
import {UtilsService} from '../../shared/services/utils.service';
import {FILE_SIZE_RESTRICTIONS} from '../../shared/models/upload-file-restrictions.model';
import {delay, filter, first, map, repeat, switchMap, tap} from 'rxjs/operators';
import {HttpEvent, HttpEventType} from '@angular/common/http';
import {REQUEST_TYPE} from '../constants/request-types.constant';
import {CardDesignRequestItemModel} from '../models/card-design-request.model';
import { TranslateService } from '@ngx-translate/core';
import { MessageService } from '../../shared/services/message-service.service';
export enum FILESCANCODE {
  SAFE = 'SAFE',
  UNSAFE = 'UNSAFE',
  PENDING = 'PENDING'
}

@Injectable({
  providedIn: 'root'
})
export class FileUploadService {

  pendingUploads: Array<string> = [];

  constructor(
    private errorHandlingHttp: ErrorHandlingHttpClient,
    protected messageService: MessageService,
    protected translateService: TranslateService) {
  }

  public requestAttachmentUploadLink(requestId: number | string, fileType: string, file: File): Observable<UploadLinkModel> {
    const sizeLimit = FILE_SIZE_RESTRICTIONS.GLOBAL;
    if (UtilsService.isFileFitsBySize(file.size, sizeLimit)) {
      return this.errorHandlingHttp.post(API.createAttachmentUploadLink(requestId, fileType), { fileName: file.name });
    }
    return throwError(new Error(
      `Unable to upload ${file.name} as it is larger than ${sizeLimit.sizeNumber} ${sizeLimit.sizeType}`
    ));
  }

  public overwriteFile(uploadUrl: string, file: File): Observable<any> {
    return this.errorHandlingHttp.put(uploadUrl, file, {
      headers: {
        'x-ms-blob-type': 'BlockBlob',
        'x-ms-version': '2019-12-12',
      }
    });
  }

  public uploadFileWithProgress(uploadLink: UploadLinkModel, file: File): Observable<any> {
    this.pendingUploads.push(uploadLink.uploadId);
    return this.errorHandlingHttp.put(uploadLink.uploadUrl, file, {
      headers: {
        'x-ms-blob-type': 'BlockBlob',
        'x-ms-client-request-id': uploadLink.uploadId,
        'x-ms-version': '2019-12-12',
      },
      reportProgress: true,
      observe: 'events',
    }).pipe(switchMap(event => {
      const uploadIndex = this.pendingUploads.indexOf(uploadLink.uploadId);
      const uploadCanceled = uploadIndex === -1;
      if (event.type === HttpEventType.Response && !uploadCanceled) {
        this.pendingUploads.splice(uploadIndex, 1);
        return of(event);
      }
      if (uploadCanceled) {
        return throwError({
          error: 'Upload canceled!'
        });
      }
      return of(event);
    }));
  }

  scanUploadFile(uploadId, fileName): Observable<any>{
    return this.errorHandlingHttp.get(API.scanAttachmentUploadFile(uploadId)).pipe(
      delay(1000),
      repeat(),
      filter(fileValidationResponse => fileValidationResponse.code !== FILESCANCODE.PENDING),
      tap(fileValidationResponse => {
        if (fileValidationResponse.code === FILESCANCODE.UNSAFE) {
          this.messageService.showSystemMessage('error', null,
            this.translateService.instant('common.errors.file-scan-error', { fileName })
          );
          return throwError(new Error('Upload canceled!'));
        }
      }),
      first(),
      map(fileValidationResponse => fileValidationResponse),
    );
  }

  public cancelUploads() {
    this.pendingUploads = [];
  }

// Common Upload Artwork functionality
  public uploadArtworkForRequestItem(file: File,
                                     requestId: number | string,
                                     requestType: REQUEST_TYPE,
                                     attachmentFileType: string,
                                     requestItemId?: number): Observable<HttpEvent<CardDesignRequestItemModel>> {
    let createUploadItemArtworkUrl: string;
    let shouldRequestHasFileTypeParameter: boolean;
    switch (requestType) {
      case REQUEST_TYPE.CPR: {
        createUploadItemArtworkUrl = API.createAttachment(requestId as string);
        shouldRequestHasFileTypeParameter = true;
        break;
      }
      case REQUEST_TYPE.TPPR: {
        createUploadItemArtworkUrl = API.createThirdPartyArtwork(requestId as string);
        break;
      }
      case REQUEST_TYPE.PPR: {
        createUploadItemArtworkUrl = API.createPopProductionArtwork(requestId as string);
        break;
      }
      case REQUEST_TYPE.FSR: {
        createUploadItemArtworkUrl = API.createFsrArtwork(requestId as string);
        break;
      }
      case REQUEST_TYPE.BPR: {
        createUploadItemArtworkUrl = API.createBifArtwork(requestId as string);
        break;
      }
      case REQUEST_TYPE.IPR: {
        createUploadItemArtworkUrl = API.createIHProductionArtwork(requestId as string);
        break;
      }
    }
    return this.uploadAndCreateAttachment(
      file, requestId, createUploadItemArtworkUrl, attachmentFileType, shouldRequestHasFileTypeParameter);
  }

  public uploadAndCreateAttachment(file: File,
                                   requestId: number | string,
                                   createAttachmentUrl: string,
                                   attachmentFileType: string,
                                   shouldRequestHasFileTypeParameter?: boolean) {
    return this.requestAttachmentUploadLink(requestId, attachmentFileType, file).pipe(
      switchMap(uploadLink => this.uploadFileWithProgress(uploadLink, file).pipe(
        switchMap(event => {
          if (event.type === HttpEventType.Response) {
            return this.scanUploadFile(uploadLink.uploadId, file.name).pipe(
              switchMap(res => {
                if(res.code === FILESCANCODE.SAFE){
                  return this.createAttachmentRequest(
                    createAttachmentUrl, uploadLink.uploadId, shouldRequestHasFileTypeParameter, attachmentFileType);
                }
              })
            );
          }
          return of(event);
        })
      ))
    );
  }

  public createAttachmentRequest(
    createAttachmentUrl: string,
    uploadId: string,
    shouldRequestHasFileTypeParameter: boolean,
    fileType: string) {
    return this.errorHandlingHttp.post(createAttachmentUrl, null,
      {
        reportProgress: true,
        observe: 'events',
        params: shouldRequestHasFileTypeParameter ? {uploadId, fileType} : {uploadId},
      });
  }

  public deleteArtwork(requestId: string | number,
                       requestItemId: string | number,
                       attachmentId: string | number,
                       requestType: REQUEST_TYPE): Observable<void> {
    let apiUrl: string;
    switch (requestType) {
      case REQUEST_TYPE.CPR: {
        apiUrl = API.deleteArtwork(requestId, attachmentId);
        break;
      }
      case REQUEST_TYPE.PPR: {
        apiUrl = API.ppDeleteArtwork(requestId, requestItemId, attachmentId);
        break;
      }
      case REQUEST_TYPE.TPPR: {
        apiUrl = API.thirdPartyDeleteArtwork(requestId, attachmentId);
        break;
      }
      case REQUEST_TYPE.FSR: {
        apiUrl = API.fsrDeleteArtwork(requestId, attachmentId);
        break;
      }
      case REQUEST_TYPE.BPR: {
        apiUrl = API.bifDeleteArtwork(requestId, attachmentId);
        break;
      }
      case REQUEST_TYPE.IPR: {
        apiUrl = API.ihProductionDeleteArtwork(requestId, attachmentId);
        break;
      }
    }
    return this.errorHandlingHttp.delete<void>(apiUrl);
  }

}
